import React, { useContext, useEffect } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import SideNavV2 from "../../components/SideNav/SideNavV2";
import { DataContext } from "../../context/Data";
import NoRouteV2 from "../../components/StatusPages/NoRouteV2";
import { AstratoContext } from "../../context/Astrato";
import { AuthContext } from "../../context/Authorization";
import getIframeLinkByNavlink from "../../utils/getIframeLinkByNavlink";
import { LoggingContext } from "../../context/Logger";
import LiveData from "../../components/LiveData";
import APIAdministration from "./APIAdministration";
import UserAdministration from "./UserAdministration";

// Route permission prefix that marks an account-scoped (non role-based) gate,
// e.g. "DTC_ACCT_ADMIN" -> requires "${account}_ACCT_ADMIN", "DTC_ACCT_USERS_ADMIN" -> requires
// "${account}_ACCT_USERS_ADMIN". Each is independent — holding one does NOT grant the other.
const ACCOUNT_LEVEL_PREFIX = "DTC_ACCT_";

export default function DTC_V2() {
  const { AllowedTo, useAuth, authData } = React.useContext(AuthContext);
  const { userHasPermission, accountHasPermission, userHasUserPermission } = useAuth();
  const { v2Routes, account, loadingChangeSpace } = useContext(DataContext);
  const { iframeSrc, isAuthenticated, iframeRef } = useContext(AstratoContext);
  const { log } = React.useContext(LoggingContext);

  const stringStaging = window.location.origin.includes("staging") ? "_staging" : "";

  // List of the components available
  const componentList = {
    LiveData: LiveData,
    APIAdministration: APIAdministration,
    UserAdministration: UserAdministration,
  };

  // Fc to get the component based on the name
  const getComponent = (name) => {
    const ComponentToRender = componentList[name];
    if (!ComponentToRender) {
      return <div>"{name}" not found.</div>;
    }

    return <ComponentToRender />;
  };

  useEffect(() => {
    if (iframeRef.current && iframeRef.current.src !== iframeSrc) {
      iframeRef.current.src = iframeSrc;
    }
  }, [iframeSrc]);

  function getIframeLink(navlink) {
    let result = getIframeLinkByNavlink(v2Routes[`dtc_routes${stringStaging}`], navlink);
    if (result) {
      return { ...result, navlink: "" };
    }
  }

  if (!userHasPermission(["ROUTE_DTC_V2_USER"], account)) {
    return <NoRouteV2 />;
  }

  const liveDataComponent = () => {
    const acc = authData.accounts[account];

    const hasAccess = accountHasPermission("VIEW_LIVE_DATA", acc);

    if (!hasAccess) return;

    return getComponent("LiveData");
  };

  const nonKpiRoutes = v2Routes.hasOwnProperty(`dtc_routes${stringStaging}`)
    ? v2Routes[`dtc_routes${stringStaging}`]
    : [];

  const hasKpiAccess = userHasUserPermission("DTC_NAV_KPI_DASHBOARD");

  // Account-level (non role-based) gate: GLOBAL_ADMIN always passes. Otherwise the user must
  // hold the EXACT "${account}_<SUFFIX>" permission for this route — ACCT_ADMIN does not grant
  // ACCT_USERS_ADMIN routes or vice versa.
  const hasAccountLevelAccess = (permission) => {
    if (!permission?.startsWith(ACCOUNT_LEVEL_PREFIX)) return false;
    if (authData?.user?.protected?.roles?.includes("GLOBAL_ADMIN")) return true;
    const suffix = permission.slice("DTC_".length); // "ACCT_ADMIN" | "ACCT_USERS_ADMIN" | ...
    return userHasPermission(`${account}_${suffix}`);
  };

  const firstNonKpiNavlink = nonKpiRoutes
    .flatMap((r) => {
      const hasAccess = r.permission?.startsWith(ACCOUNT_LEVEL_PREFIX)
        ? hasAccountLevelAccess(r.permission)
        : userHasUserPermission(r.permission);
      if (!hasAccess) return [];
      return r.links?.filter((l) => l?.navlink) ?? [];
    })
    .find((l) => l?.navlink)?.navlink;

  const allDtcRoutes = v2Routes.hasOwnProperty(`dtc_routes${stringStaging}`)
    ? [
        { links: [getIframeLink("kpi_dashboard")], permission: "DTC_NAV_KPI_DASHBOARD" },
        ...nonKpiRoutes,
      ]
    : [];

  return (
    <div className="flex flex-grow overflow-hidden h-[calc(100vh-63px)]">
      <SideNavV2 route="dtc" />
      <main className="p-2 h-full w-full">
        <div className="flex flex-col h-full w-full">
          <Routes>
            {allDtcRoutes.map((r) => {
              const hasAccess = r.permission?.startsWith(ACCOUNT_LEVEL_PREFIX)
                ? hasAccountLevelAccess(r.permission)
                : userHasUserPermission(r.permission);

              if (r.permission === "DTC_NAV_KPI_DASHBOARD") {
                const kpiElement = hasAccess ? null : (
                  nonKpiRoutes.length === 0 ? null :
                  firstNonKpiNavlink ? <Navigate to={firstNonKpiNavlink} replace /> : <NoRouteV2 />
                );
                if (!hasAccess) {
                  return r.links.map((l) => (
                    <Route key={l?.navlink ?? "kpi"} path={l?.navlink ?? ""} element={kpiElement} />
                  ));
                }
              }

              if (!hasAccess) return null;

              return r.links.map((l) => {
                const element = (
                  <>
                    {l.iframeLink ? (
                      <div className="w-full h-full">
                        {!isAuthenticated || loadingChangeSpace ? (
                          <div className="w-full h-full flex justify-center items-center">
                            <svg
                              className="animate-spin h-5 w-5 mr-3 text-main"
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                            >
                              <circle
                                className="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="4"
                              ></circle>
                              <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                              ></path>
                            </svg>
                          </div>
                        ) : (
                          <>
                            {l?.liveData && liveDataComponent()}
                            <iframe
                              ref={iframeRef}
                              className="w-full h-full"
                              title="iframeAstratoDTC"
                              src={iframeSrc || l.iframeLink}
                              onLoad={(e) => {
                                log.debug("Iframe DTC loaded with new src!", e);
                              }}
                            />
                          </>
                        )}
                      </div>
                    ) : l.component ? (
                      <div className="w-full h-full">{getComponent(l.component)}</div>
                    ) : (
                      <div className="w-full h-full flex justify-center items-center">
                        <p>No content configured for this route.</p>
                      </div>
                    )}
                  </>
                );

                return (
                  <Route
                    key={l.navlink}
                    path={l.navlink}
                    element={<div className="w-full h-full">{element}</div>}
                  />
                );
              });
            })}
            <Route path="*" element={<NoRouteV2 />} />
          </Routes>
        </div>
      </main>
    </div>
  );
}
